import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { useAuth } from '../lib/AuthContext';
import orangeLogo from '../assets/orangelog.png';
import AdSlot from '../components/AdSlot';

const T = {
  accent:  '#dd3333',
  header:  '#111111',
  border:  '#e8e8e8',
  bodyText:'#444',
  headText:'#111',
  meta:    '#777',
  lightBg: '#f4f4f4',
  white:   '#fff',
};

const GENRE_COLOR = {
  'National News': '#dd3333',
  'State News':    '#4db2ec',
  'Local News':    '#27ae60',
  'Sports':        '#e67e22',
  'Technology':    '#8e44ad',
};

const GENRE_ROUTES = [
  { label: 'National News', to: '/national-news', color: '#dd3333' },
  { label: 'State News',    to: '/state-news',    color: '#4db2ec' },
  { label: 'Sports',        to: '/sports',        color: '#e67e22' },
  { label: 'Technology',    to: '/technology',    color: '#8e44ad' },
  { label: 'Local News',    to: '/local-news',    color: '#27ae60' },
];

const GENRE_DESC = {
  'National News': 'Top stories curated from local, state, and national publishers and journalists.',
  'State News':    'Articles filtered to your state, from publishers operating in your region.',
  'Sports':        'Game recaps, analysis, and sports coverage from independent sports journalists.',
  'Technology':    'Tech news, product reviews, and digital culture from independent voices.',
  'Local News':    'Stories from publishers in your area.',
};

function fmtDate(d) {
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function ArticleCard({ article, onClick }) {
  const [hov, setHov] = useState(false);
  const color = GENRE_COLOR[article.genre_tree?.main] || T.accent;
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{ cursor: 'pointer', marginBottom: '20px', paddingBottom: '20px', borderBottom: `1px solid ${T.border}` }}
    >
      {article.headline_image && (
        <div style={{ aspectRatio: '16/9', overflow: 'hidden', marginBottom: '10px', background: '#e8e8e8' }}>
          <img src={article.headline_image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top', transition: 'transform 0.4s', transform: hov ? 'scale(1.03)' : 'scale(1)' }} />
        </div>
      )}
      <div style={{ fontSize: '10px', fontWeight: '800', color, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '5px', fontFamily: 'Arial, sans-serif' }}>
        {article.genre_tree?.main}
      </div>
      <div style={{ fontSize: '18px', fontWeight: '700', color: hov ? color : T.headText, lineHeight: '26px', marginBottom: '6px', transition: 'color 0.13s', fontFamily: 'Georgia, serif' }}>
        {article.title}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        {article.hero_image && (
          <img src={article.hero_image} alt={article.company_name} style={{ height: '16px', width: '16px', borderRadius: '50%', objectFit: 'cover' }} />
        )}
        <span style={{ fontSize: '12px', color: T.meta, fontFamily: 'Arial, sans-serif' }}>
          {article.company_name} · {fmtDate(article.date_created)}
        </span>
      </div>
    </div>
  );
}

export default function GenreFeed({ genre }) {
  const navigate = useNavigate();
  const { accessToken: token } = useAuth();
  const username = localStorage.getItem('username');
  const userType = localStorage.getItem('userType');

  const [articles,    setArticles]    = useState([]);
  const [total,       setTotal]       = useState(0);
  const [page,        setPage]        = useState(1);
  const [loading,     setLoading]     = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [isMobile,    setIsMobile]    = useState(() => window.innerWidth < 768);

  const PAGE_SIZE  = 20;
  const genreColor = GENRE_COLOR[genre] || T.accent;

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    setArticles([]);
    setPage(1);
    setTotal(0);
    loadPage(1, true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [genre]);

  const loadPage = async (pageNum, reset = false) => {
    if (reset) setLoading(true); else setLoadingMore(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get('/api/articles/genre-feed', {
        params: { genre, page: pageNum, limit: PAGE_SIZE },
        headers,
      });
      setArticles(prev => reset ? res.data.articles : [...prev, ...res.data.articles]);
      setTotal(res.data.total);
      setPage(pageNum);
    } catch (_) {}
    finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleArticleClick = (article) => {
    const dest = `/${encodeURIComponent(article.company_name)}?a=${article._id}`;
    if (!token) { navigate(`/login?next=${encodeURIComponent(dest)}`); return; }
    navigate(dest);
  };

  const siloHref = token && userType === 'user' ? `/${username}` : null;

  return (
    <>
    <Helmet>
      <title>{genre} — PubSilo</title>
      <meta name="description" content={`Read ${genre} articles from publishers on PubSilo.`} />
    </Helmet>
    <div style={{ minHeight: '100vh', background: T.white, fontFamily: 'Georgia, serif', color: T.bodyText }}>

      {/* Navbar */}
      {isMobile ? (
        <nav style={{ position: 'sticky', top: 0, zIndex: 1000, boxShadow: '0 2px 12px rgba(0,0,0,0.6)' }}>
          <div style={{ display: 'flex', height: '70px' }}>
            <div style={{ width: '50%', background: '#F41238', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <img src={orangeLogo} alt="PubSilo" style={{ height: '90%', maxWidth: '100%', objectFit: 'contain', cursor: 'pointer' }} onClick={() => navigate('/')} />
            </div>
            <div style={{ width: '50%', background: '#13273F', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
              <div style={{ fontSize: '9px', fontWeight: '900', letterSpacing: '0.1em', textTransform: 'uppercase', color: T.white, background: genreColor, padding: '2px 8px', fontFamily: 'Arial, sans-serif' }}>Section</div>
              <div style={{ color: T.white, fontSize: '18px', fontWeight: '900', fontFamily: 'Georgia, serif', lineHeight: 1 }}>{genre}</div>
            </div>
          </div>
          <div style={{ background: '#0d1f33', height: '42px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 14px', borderTop: '1px solid #1a2f45' }}>
            {token ? (
              siloHref ? (
                <Link to={siloHref} style={{ background: T.accent, color: T.white, padding: '6px 14px', borderRadius: '4px', textDecoration: 'none', fontSize: '12px', fontWeight: '700', fontFamily: 'Arial, sans-serif' }}>My Silo</Link>
              ) : null
            ) : (
              <>
                <Link to="/login" style={{ color: '#ccc', textDecoration: 'none', fontSize: '12px', fontFamily: 'Arial, sans-serif', fontWeight: '700', padding: '6px 12px', border: '1px solid #3a3a3a', borderRadius: '4px' }}>Log In</Link>
                <Link to="/signup/role" style={{ background: T.accent, color: T.white, textDecoration: 'none', fontSize: '12px', fontFamily: 'Arial, sans-serif', fontWeight: '700', padding: '6px 14px', borderRadius: '4px' }}>Sign Up</Link>
              </>
            )}
          </div>
        </nav>
      ) : (
        <nav style={{ background: T.header, display: 'grid', gridTemplateColumns: '220px 1fr 300px', gridTemplateRows: '110px 36px', position: 'sticky', top: 0, zIndex: 1000, boxShadow: '0 2px 12px rgba(0,0,0,0.6)' }}>
          <div style={{ gridColumn: 1, gridRow: '1 / 3', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F41238' }}>
            <img src={orangeLogo} alt="PubSilo" style={{ height: '90%', maxWidth: '210px', objectFit: 'contain', cursor: 'pointer' }} onClick={() => navigate('/')} />
          </div>
          <div style={{ gridColumn: 2, gridRow: 1, background: '#13273F', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
            <div style={{ fontSize: '10px', fontWeight: '900', letterSpacing: '0.1em', textTransform: 'uppercase', color: T.white, background: genreColor, padding: '3px 10px', fontFamily: 'Arial, sans-serif' }}>Section</div>
            <div style={{ color: T.white, fontSize: '30px', fontWeight: '900', fontFamily: 'Georgia, serif', lineHeight: 1 }}>{genre}</div>
          </div>
          <div style={{ gridColumn: 2, gridRow: 2, background: '#13273F', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {GENRE_ROUTES.map(({ label, to, color }) => (
              <Link key={label} to={to}
                style={{ color: label === genre ? T.white : '#aaa', textDecoration: 'none', fontSize: '11px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.08em', padding: '0 16px', height: '100%', display: 'flex', alignItems: 'center', fontFamily: 'Arial, sans-serif', background: label === genre ? genreColor : 'transparent', transition: 'background 0.12s, color 0.12s' }}
                onMouseEnter={e => { if (label !== genre) { e.currentTarget.style.background = color; e.currentTarget.style.color = T.white; } }}
                onMouseLeave={e => { if (label !== genre) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#aaa'; } }}
              >{label}</Link>
            ))}
          </div>
          <div style={{ gridColumn: 3, gridRow: '1 / 3', background: '#13273F', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
            {siloHref ? (
              <Link to={siloHref} style={{ background: T.accent, color: T.white, padding: '9px 18px', borderRadius: '5px', textDecoration: 'none', fontSize: '13px', fontWeight: '700', fontFamily: 'Arial, sans-serif', whiteSpace: 'nowrap' }}>My Silo</Link>
            ) : (
              <>
                <Link to="/login" style={{ color: '#ccc', textDecoration: 'none', fontSize: '13px', fontFamily: 'Arial, sans-serif', fontWeight: '700', padding: '8px 15px', border: '1px solid #3a3a3a', borderRadius: '5px' }} onMouseEnter={e => { e.currentTarget.style.background = '#222'; e.currentTarget.style.color = T.white; }} onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#ccc'; }}>Login</Link>
                <Link to="/signup/role" style={{ background: T.accent, color: T.white, textDecoration: 'none', fontSize: '13px', fontFamily: 'Arial, sans-serif', fontWeight: '700', padding: '9px 18px', borderRadius: '5px', whiteSpace: 'nowrap' }}>Get Started</Link>
              </>
            )}
          </div>
        </nav>
      )}

      {/* Body */}
      <div style={{ display: 'flex', alignItems: 'flex-start' }}>

        {/* Left sidebar */}
        <div style={{ width: '220px', flexShrink: 0, borderRight: `1px solid ${T.border}`, minHeight: 'calc(100vh - 146px)', padding: '16px 12px' }}>
          <div style={{ fontSize: '11px', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '0.09em', color: '#888', paddingBottom: '8px', borderBottom: `2px solid ${T.accent}`, marginBottom: '12px', fontFamily: 'Arial, sans-serif' }}>
            Topics
          </div>
          {GENRE_ROUTES.map(({ label, to, color }) => (
            <Link key={label} to={to}
              style={{ display: 'block', padding: '8px 10px', marginBottom: '3px', borderRadius: '4px', textDecoration: 'none', fontSize: '13px', fontWeight: label === genre ? '800' : '500', fontFamily: 'Arial, sans-serif', color: label === genre ? T.white : T.bodyText, background: label === genre ? color : 'transparent', transition: 'background 0.12s' }}
              onMouseEnter={e => { if (label !== genre) e.currentTarget.style.background = T.lightBg; }}
              onMouseLeave={e => { if (label !== genre) e.currentTarget.style.background = 'transparent'; }}
            >
              {label}
            </Link>
          ))}
          <div style={{ marginTop: '16px', paddingTop: '12px', borderTop: `1px solid ${T.border}` }}>
            <Link to="/" style={{ display: 'block', textAlign: 'center', padding: '8px', background: T.lightBg, color: T.bodyText, textDecoration: 'none', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.07em', borderRadius: '4px', fontFamily: 'Arial, sans-serif' }}>
              ← All News
            </Link>
          </div>
        </div>

        {/* Main content */}
        <div style={{ flex: 1, minWidth: 0, padding: '24px 28px', borderRight: `1px solid ${T.border}` }}>
          <div style={{ borderBottom: `3px solid ${genreColor}`, paddingBottom: '8px', marginBottom: '20px', display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
            <h1 style={{ fontSize: '20px', fontWeight: '900', color: T.headText, margin: 0, fontFamily: 'Arial, sans-serif', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{genre}</h1>
            {!loading && total > 0 && (
              <span style={{ fontSize: '12px', color: T.meta, fontFamily: 'Arial, sans-serif' }}>
                {total} article{total !== 1 ? 's' : ''}{genre === 'State News' && token ? ' · your state' : ''}
              </span>
            )}
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', color: '#aaa', padding: '3rem', fontFamily: 'Arial, sans-serif' }}>Loading…</div>
          ) : articles.length === 0 ? (
            <div style={{ textAlign: 'center', color: T.meta, padding: '3rem', fontFamily: 'Arial, sans-serif' }}>
              No {genre.toLowerCase()} articles yet.
            </div>
          ) : (
            <>
              {articles.flatMap((article, i) => {
                const adAudience = { 'National News': 'national', 'State News': 'state', 'Sports': 'national', 'Technology': 'national' }[genre] || 'national';
                const card = <ArticleCard key={article._id} article={article} onClick={() => handleArticleClick(article)} />;
                if (i === 3 && isMobile) {
                  return [card, <AdSlot key="mobile-ad-genre" genre={genre} audience={adAudience} mobile />];
                }
                return [card];
              })}
              {articles.length < total && (
                <button
                  onClick={() => loadPage(page + 1)}
                  disabled={loadingMore}
                  style={{ display: 'block', margin: '0 auto', padding: '0.6rem 2rem', background: T.headText, color: T.white, border: 'none', borderRadius: '4px', fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.07em', cursor: loadingMore ? 'not-allowed' : 'pointer', opacity: loadingMore ? 0.7 : 1, fontFamily: 'Arial, sans-serif' }}
                >
                  {loadingMore ? 'Loading…' : 'Load More'}
                </button>
              )}
            </>
          )}
        </div>

        {/* Right sidebar — ad zone (desktop only) */}
        {!isMobile && (
          <div style={{ width: '300px', flexShrink: 0, minHeight: 'calc(100vh - 146px)', padding: '16px 12px' }}>
            <AdSlot genre={genre} audience={{ 'National News': 'national', 'State News': 'state', 'Sports': 'national', 'Technology': 'national' }[genre] || 'national'} />
            {!token && (
              <div style={{ background: T.lightBg, borderRadius: '6px', padding: '12px', marginTop: '20px' }}>
                <div style={{ fontSize: '12px', fontWeight: '700', color: T.headText, marginBottom: '6px', fontFamily: 'Arial, sans-serif' }}>Join PubSilo</div>
                <p style={{ fontSize: '11px', color: T.meta, margin: '0 0 10px', lineHeight: '1.5', fontFamily: 'Arial, sans-serif' }}>Get full access to articles from independent publishers.</p>
                <Link to="/signup/role" style={{ display: 'block', textAlign: 'center', padding: '8px', background: T.accent, color: T.white, textDecoration: 'none', fontSize: '11px', fontWeight: '700', borderRadius: '4px', fontFamily: 'Arial, sans-serif' }}>
                  Sign Up Free
                </Link>
              </div>
            )}
          </div>
        )}

      </div>
    </div>
    </>
  );
}
